// 数据缓存管理工具
class DataManager {
  constructor() {
    this.STORAGE_KEYS = {
      PLAYLISTS: 'tunefree_playlists',
      FAVORITES: 'tunefree_favorites',
      RECENT: 'tunefree_recent',
      PLAY_QUEUE: 'tunefree_play_queue',
      CURRENT_INDEX: 'tunefree_current_index'
    };
    
    // 初始化默认数据
    this.initializeDefaultData();
  }

  // Base64 编码
  encode(data) {
    try {
      const jsonString = JSON.stringify(data);
      return btoa(unescape(encodeURIComponent(jsonString)));
    } catch (error) {
      console.error('数据编码失败:', error);
      return null;
    }
  }

  // Base64 解码
  decode(encodedData) {
    try {
      const jsonString = decodeURIComponent(escape(atob(encodedData)));
      return JSON.parse(jsonString);
    } catch (error) {
      console.error('数据解码失败:', error);
      return null;
    }
  }

  // 获取缓存数据
  getData(key) {
    try {
      const encodedData = localStorage.getItem(key);
      if (!encodedData) return null;
      return this.decode(encodedData);
    } catch (error) {
      console.error('获取缓存数据失败:', error);
      return null;
    }
  }

  // 保存缓存数据
  setData(key, data) {
    try {
      const encodedData = this.encode(data);
      if (encodedData) {
        localStorage.setItem(key, encodedData);
        return true;
      }
      return false;
    } catch (error) {
      console.error('保存缓存数据失败:', error);
      return false;
    }
  }

  // 初始化默认数据
  initializeDefaultData() {
    // 初始化歌单列表
    if (!this.getData(this.STORAGE_KEYS.PLAYLISTS)) {
      const defaultPlaylists = [
        {
          id: 'playlist_1',
          name: '工作专用',
          description: '适合工作时听的音乐',
          createdAt: Date.now(),
          songs: []
        },
        {
          id: 'playlist_2',
          name: '运动时光',
          description: '运动健身专用歌单',
          createdAt: Date.now(),
          songs: []
        }
      ];
      this.setData(this.STORAGE_KEYS.PLAYLISTS, defaultPlaylists);
    }

    // 初始化我喜欢的音乐
    if (!this.getData(this.STORAGE_KEYS.FAVORITES)) {
      this.setData(this.STORAGE_KEYS.FAVORITES, []);
    }

    // 初始化最近播放
    if (!this.getData(this.STORAGE_KEYS.RECENT)) {
      this.setData(this.STORAGE_KEYS.RECENT, []);
    }

    // 初始化播放队列
    if (!this.getData(this.STORAGE_KEYS.PLAY_QUEUE)) {
      this.setData(this.STORAGE_KEYS.PLAY_QUEUE, []);
    }

    // 初始化当前播放索引
    if (!this.getData(this.STORAGE_KEYS.CURRENT_INDEX)) {
      this.setData(this.STORAGE_KEYS.CURRENT_INDEX, -1);
    }
  }

  // 获取所有歌单
  getPlaylists() {
    return this.getData(this.STORAGE_KEYS.PLAYLISTS) || [];
  }

  // 创建新歌单
  createPlaylist(name, description = '') {
    const playlists = this.getPlaylists();
    const newPlaylist = {
      id: `playlist_${Date.now()}`,
      name,
      description,
      createdAt: Date.now(),
      songs: []
    };
    playlists.push(newPlaylist);
    this.setData(this.STORAGE_KEYS.PLAYLISTS, playlists);
    return newPlaylist;
  }

  // 删除歌单
  deletePlaylist(playlistId) {
    const playlists = this.getPlaylists();
    const filteredPlaylists = playlists.filter(p => p.id !== playlistId);
    return this.setData(this.STORAGE_KEYS.PLAYLISTS, filteredPlaylists);
  }

  // 获取歌单详情
  getPlaylist(playlistId) {
    const playlists = this.getPlaylists();
    return playlists.find(p => p.id === playlistId);
  }

  // 添加歌曲到歌单
  addSongToPlaylist(playlistId, song) {
    const playlists = this.getPlaylists();
    const playlist = playlists.find(p => p.id === playlistId);
    
    if (!playlist) return false;
    
    // 检查歌曲是否已存在
    const songExists = playlist.songs.some(s => s.id === song.id && s.platform === song.platform);
    if (songExists) return false;
    
    // 添加歌曲（不包含封面信息）
    const songData = {
      id: song.id,
      name: song.name,
      artist: song.artist,
      album: song.album,
      platform: song.platform,
      addedAt: Date.now()
    };
    
    playlist.songs.push(songData);
    return this.setData(this.STORAGE_KEYS.PLAYLISTS, playlists);
  }

  // 从歌单移除歌曲
  removeSongFromPlaylist(playlistId, songId, platform) {
    const playlists = this.getPlaylists();
    const playlist = playlists.find(p => p.id === playlistId);
    
    if (!playlist) return false;
    
    playlist.songs = playlist.songs.filter(s => !(s.id === songId && s.platform === platform));
    return this.setData(this.STORAGE_KEYS.PLAYLISTS, playlists);
  }

  // 获取我喜欢的音乐
  getFavorites() {
    return this.getData(this.STORAGE_KEYS.FAVORITES) || [];
  }

  // 添加到我喜欢的音乐
  addToFavorites(song) {
    const favorites = this.getFavorites();
    
    // 检查是否已收藏
    const exists = favorites.some(s => s.id === song.id && s.platform === song.platform);
    if (exists) return false;
    
    const songData = {
      id: song.id,
      name: song.name,
      artist: song.artist,
      album: song.album,
      platform: song.platform,
      addedAt: Date.now()
    };
    
    favorites.unshift(songData); // 添加到最前面
    return this.setData(this.STORAGE_KEYS.FAVORITES, favorites);
  }

  // 从我喜欢的音乐移除
  removeFromFavorites(songId, platform) {
    const favorites = this.getFavorites();
    const filteredFavorites = favorites.filter(s => !(s.id === songId && s.platform === platform));
    return this.setData(this.STORAGE_KEYS.FAVORITES, filteredFavorites);
  }

  // 检查歌曲是否已收藏
  isFavorite(songId, platform) {
    const favorites = this.getFavorites();
    return favorites.some(s => s.id === songId && s.platform === platform);
  }

  // 添加到最近播放
  addToRecent(song) {
    const recent = this.getData(this.STORAGE_KEYS.RECENT) || [];
    
    // 移除已存在的记录
    const filteredRecent = recent.filter(s => !(s.id === song.id && s.platform === song.platform));
    
    const songData = {
      id: song.id,
      name: song.name,
      artist: song.artist,
      album: song.album,
      platform: song.platform,
      playedAt: Date.now()
    };
    
    // 添加到最前面，保持最多50首
    filteredRecent.unshift(songData);
    if (filteredRecent.length > 50) {
      filteredRecent.splice(50);
    }
    
    return this.setData(this.STORAGE_KEYS.RECENT, filteredRecent);
  }

  // 获取最近播放
  getRecent() {
    return this.getData(this.STORAGE_KEYS.RECENT) || [];
  }

  // 清除所有最近播放记录
  clearRecent() {
    return this.setData(this.STORAGE_KEYS.RECENT, []);
  }

  // ================== 播放队列管理 ==================

  // 获取播放队列
  getPlayQueue() {
    return this.getData(this.STORAGE_KEYS.PLAY_QUEUE) || [];
  }

  // 添加歌曲到播放队列
  addToPlayQueue(song) {
    const queue = this.getPlayQueue();
    
    // 检查歌曲是否已存在于队列中
    const exists = queue.some(s => s.id === song.id && s.platform === song.platform);
    if (exists) return queue;
    
    const songData = {
      id: song.id,
      name: song.name,
      artist: song.artist,
      album: song.album,
      platform: song.platform,
      addedAt: Date.now()
    };
    
    queue.push(songData);
    this.setData(this.STORAGE_KEYS.PLAY_QUEUE, queue);
    return queue;
  }

  // 批量添加歌曲到播放队列
  addSongsToPlayQueue(songs) {
    const queue = this.getPlayQueue();
    
    songs.forEach(song => {
      // 检查歌曲是否已存在于队列中
      const exists = queue.some(s => s.id === song.id && s.platform === song.platform);
      if (!exists) {
        const songData = {
          id: song.id,
          name: song.name,
          artist: song.artist,
          album: song.album,
          platform: song.platform,
          addedAt: Date.now()
        };
        queue.push(songData);
      }
    });
    
    this.setData(this.STORAGE_KEYS.PLAY_QUEUE, queue);
    return queue;
  }

  // 从播放队列移除歌曲
  removeFromPlayQueue(index) {
    const queue = this.getPlayQueue();
    if (index >= 0 && index < queue.length) {
      queue.splice(index, 1);
      this.setData(this.STORAGE_KEYS.PLAY_QUEUE, queue);
      
      // 调整当前播放索引
      const currentIndex = this.getCurrentIndex();
      if (index < currentIndex) {
        this.setCurrentIndex(currentIndex - 1);
      } else if (index === currentIndex && queue.length > 0) {
        // 如果删除的是当前播放的歌曲，调整索引
        const newIndex = index < queue.length ? index : queue.length - 1;
        this.setCurrentIndex(newIndex);
      } else if (queue.length === 0) {
        this.setCurrentIndex(-1);
      }
    }
    return queue;
  }

  // 清空播放队列
  clearPlayQueue() {
    this.setData(this.STORAGE_KEYS.PLAY_QUEUE, []);
    this.setCurrentIndex(-1);
    return [];
  }

  // 获取当前播放索引
  getCurrentIndex() {
    const index = this.getData(this.STORAGE_KEYS.CURRENT_INDEX);
    return index !== null ? index : -1;
  }

  // 设置当前播放索引
  setCurrentIndex(index) {
    this.setData(this.STORAGE_KEYS.CURRENT_INDEX, index);
  }

  // 清空所有数据（用于重置）
  clearAllData() {
    Object.values(this.STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    this.initializeDefaultData();
  }

  // 获取设置
  getSettings() {
    const defaultSettings = {
      crossfade: true,
      highQuality: false,
      playMode: 'list',
      lyricsFontSize: 'medium',
      desktopNotifications: true,
      analytics: false
    };
    
    const savedSettings = this.getData('tunefree_settings');
    return savedSettings ? { ...defaultSettings, ...savedSettings } : defaultSettings;
  }

  // 保存设置
  saveSettings(settings) {
    return this.setData('tunefree_settings', settings);
  }

  // 导出所有数据
  exportData() {
    const allData = {};
    Object.entries(this.STORAGE_KEYS).forEach(([name, key]) => {
      allData[name] = this.getData(key);
    });
    return allData;
  }

  // 导入数据
  importData(data) {
    try {
      Object.entries(data).forEach(([name, value]) => {
        const key = this.STORAGE_KEYS[name];
        if (key && value) {
          this.setData(key, value);
        }
      });
      return true;
    } catch (error) {
      console.error('导入数据失败:', error);
      return false;
    }
  }
}

// 创建全局实例
const dataManager = new DataManager();

export default dataManager;
